// sttClient.js
// Exports functions to start/stop streaming microphone audio to the server STT WS
// Sends messages as JSON:
// { event: "start" }
// { event: "audio", audioChunk: "<base64>" }
// { event: "stop" }

let audioContext = null;
let processor = null;
let source = null;
let socket = null;
let isStreaming = false;
let mediaStream = null;

function floatTo16BitPCM(float32Array) {
  const l = float32Array.length;
  const buffer = new ArrayBuffer(l * 2);
  const view = new DataView(buffer);
  let offset = 0;
  for (let i = 0; i < l; i++, offset += 2) {
    let s = Math.max(-1, Math.min(1, float32Array[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return buffer;
}

function encodeBase64(buffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

// CHANGED: downsample audio to 16kHz for better STT stability/accuracy
function downsampleBuffer(buffer, inputSampleRate, outputSampleRate) {
  if (outputSampleRate === inputSampleRate) return buffer;
  const sampleRateRatio = inputSampleRate / outputSampleRate;
  const newLength = Math.round(buffer.length / sampleRateRatio);
  const result = new Float32Array(newLength);
  let offsetResult = 0;
  let offsetBuffer = 0;
  while (offsetResult < result.length) {
    const nextOffsetBuffer = Math.round((offsetResult + 1) * sampleRateRatio);
    let accum = 0, count = 0;
    for (let i = offsetBuffer; i < nextOffsetBuffer && i < buffer.length; i++) {
      accum += buffer[i];
      count++;
    }
    result[offsetResult] = accum / count;
    offsetResult++;
    offsetBuffer = nextOffsetBuffer;
  }
  return result;
}

export async function startSTT(wsUrl, options, onTranscription) {
  if (isStreaming) return;
  isStreaming = true;

  // open websocket
  socket = new WebSocket(wsUrl);
  socket.binaryType = "arraybuffer";

  socket.onopen = () => {
    console.log("STT WS open");
    socket.send(JSON.stringify({ event: "start", language: options.language }));
  };

  socket.onmessage = (ev) => {
    try {
      const msg = JSON.parse(ev.data);
      if (msg.type === "stt" && onTranscription) {
        onTranscription({
          text: msg.text,
          isPartial: msg.isPartial
        });
      }
    } catch (e) {
      // fallback if server sends plain text
      if (onTranscription) onTranscription(ev.data);
    }
  };

  socket.onclose = () => console.log("STT WS closed");

  // get mic
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  mediaStream = stream;

  // CHANGED: let browser choose native sample rate; we downsample ourselves to 16k
  audioContext = new (window.AudioContext || window.webkitAudioContext)();
  source = audioContext.createMediaStreamSource(stream);

  // Use ScriptProcessorNode to get raw PCM (fallback; AudioWorklet recommended but more code)
  const bufferSize = 4096;
  processor = audioContext.createScriptProcessor(bufferSize, 1, 1);

  processor.onaudioprocess = (e) => {
    if (!isStreaming) return;
    const channelData = e.inputBuffer.getChannelData(0); // mono

    // CHANGED: downsample to 16k before encoding to PCM16
    const inputRate = audioContext.sampleRate;
    const downsampled = downsampleBuffer(channelData, inputRate, 16000);
    const pcm16Buffer = floatTo16BitPCM(downsampled);
    const base64Chunk = encodeBase64(pcm16Buffer);

    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ event: "audio", audioChunk: base64Chunk }));
    }
  };

  source.connect(processor);
  processor.connect(audioContext.destination);
}

export function stopSTT() {
  isStreaming = false;

  if (socket && socket.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify({ event: "stop" }));
  }

  if (processor) {
    processor.disconnect();
    processor.onaudioprocess = null;
    processor = null;
  }

  if (source) {
    source.disconnect();
    source = null;
  }

  if (mediaStream) {
    mediaStream.getTracks().forEach(t => t.stop());
    mediaStream = null;
  }

  if (audioContext) {
    audioContext.close();
    audioContext = null;
  }

  if (socket) {
    socket.close();
    socket = null;
  }
}
